import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Header from '../../components/layout/Header';
import { ArrowLeft, Inbox, ClipboardList, User, ArrowRight, Repeat, Archive, SkipForward, UserX, Layers, Search, X } from 'lucide-react';
import { useCRM } from '../../context/CRMContext';
import { apiFetch } from '../../utils/api';
import './RecycleLeads.css';

const ACTION_ICON = { rotated: Repeat, dumped: Archive, skipped: SkipForward, unassigned: UserX };

const Pager = ({ meta, onPage }) => {
  if (!meta || meta.pages <= 1) return null;
  return (
    <div className="rc-pager">
      <button disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>Prev</button>
      <span>Page {meta.page} of {meta.pages}</span>
      <button disabled={meta.page >= meta.pages} onClick={() => onPage(meta.page + 1)}>Next</button>
    </div>
  );
};

const RecycleLeadDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { fetchUsers } = useCRM();

  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [statuses, setStatuses] = useState([]);

  const [tab, setTab] = useState('queue');
  const [queue, setQueue] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [logs, setLogs] = useState([]);
  const [logsMeta, setLogsMeta] = useState({ total: 0, page: 1, pages: 1 });
  const [stats, setStats] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionFilter, setActionFilter] = useState(''); // '', rotated, dumped, skipped, unassigned
  const [queueSearch, setQueueSearch] = useState('');
  const queueSearchRef = useRef('');

  const statusName = (lsid) => statuses.find(s => String(s.ls_id) === String(lsid))?.name || '—';
  const userName = (uid) => {
    const u = users.find(x => String(x.u_id) === String(uid));
    return u ? (u.name || u.username) : (uid ? `#${uid}` : 'Unassigned');
  };

  useEffect(() => {
    setLoading(true);
    apiFetch(`recycle-rules/${id}`).then(res => {
      setRule(res?.success ? (res.data?.rule || null) : null);
    }).finally(() => setLoading(false));
    fetchUsers().then(res => {
      const list = res?.success ? (Array.isArray(res.data) ? res.data : res.data?.users || []) : [];
      setUsers(list);
    });
    apiFetch('lead-statuses?include_recycle=1').then(res => {
      if (res?.success) setStatuses(res.data?.statuses || []);
    });
  }, [id, fetchUsers]);

  const loadDetail = useCallback(async (which, page = 1) => {
    if (which === 'queue') {
      setDetailLoading(true);
      try {
        const res = await apiFetch(`recycle-rules/${id}/queue?page=${page}&limit=20&search=${encodeURIComponent(queueSearchRef.current)}`);
        if (res?.success) setQueue({ items: res.data?.items || [], total: res.data?.total || 0, page: res.data?.page || 1, pages: res.data?.pages || 1 });
      } finally { setDetailLoading(false); }
    } else if (which === 'logs') {
      setDetailLoading(true);
      try {
        const res = await apiFetch(`recycle-rules/logs?rr_id=${id}&page=${page}&limit=50&action=${actionFilter}`);
        if (res?.success) {
          setLogs(res.data?.logs || []);
          setLogsMeta({ total: res.data?.total || 0, page: res.data?.page || 1, pages: res.data?.pages || 1 });
          setStats(res.data?.stats || null);
        }
      } finally { setDetailLoading(false); }
    }
  }, [id, actionFilter]);

  useEffect(() => {
    if (rule) loadDetail(tab, 1);
  }, [rule, tab, loadDetail]);

  // Debounced queue search — keeps loadDetail identity stable (uses ref).
  useEffect(() => {
    queueSearchRef.current = queueSearch;
    if (!rule || tab !== 'queue') return;
    const t = setTimeout(() => loadDetail('queue', 1), 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queueSearch]);

  const fmtCountdown = (rotateAt) => {
    const ms = new Date(rotateAt).getTime() - Date.now();
    if (ms <= 0) return 'due';
    const m = Math.floor(ms / 60000);
    if (m < 60) return `${m}m`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ${m % 60}m`;
    return `${Math.floor(h / 24)}d ${h % 24}h`;
  };

  const fmtDateTime = (dt) => {
    try { return new Date(dt).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }); }
    catch (_) { return ''; }
  };

  const logFilterChips = [
    { key: '', label: 'All', count: stats?.total, icon: Layers },
    { key: 'rotated', label: 'Rotated', count: stats?.rotated, icon: Repeat },
    { key: 'dumped', label: 'Dumped', count: stats?.dumped, icon: Archive },
    { key: 'skipped', label: 'Skipped', count: stats?.skipped, icon: SkipForward },
    { key: 'unassigned', label: 'Unassigned', count: stats?.unassigned, icon: UserX },
  ];

  const exportLogsCsv = () => {
    const header = ['Time', 'Lead', 'Action', 'From', 'To', 'Old Status', 'New Status', 'Note'];
    const rows = logs.map(l => [
      l.run_at, l.lead_name || `#${l.l_id}`, l.action, l.from_username || '', l.to_username || '',
      l.old_status_name || '', l.new_status_name || '', (l.note || '').replace(/"/g, "'"),
    ]);
    const csv = [header, ...rows].map(r => r.map(c => `"${c ?? ''}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `recycle-logs-${id}.csv`;
    a.click();
  };

  return (
    <>
      <Header
        title={rule?.rule_name || (loading ? 'Loading…' : `Rule #${id}`)}
        subtitle="Recycle rule details"
      />
      <div className="page">
        <div className="rc-detail-top">
          <button className="rc-back" onClick={() => navigate('/recycle-leads')}>
            <ArrowLeft size={16} /> Back to rules
          </button>
        </div>

        {loading ? (
          <div className="rc-loading">Loading rule…</div>
        ) : !rule ? (
          <div className="rc-no-match">
            <div className="rc-no-match__icon"><Inbox size={30} /></div>
            <h3>Rule not found</h3>
            <p>This recycle rule may have been deleted.</p>
            <button className="rc-text-btn" onClick={() => navigate('/recycle-leads')}>Back to rules</button>
          </div>
        ) : (
          <div className="rc-detail-panel">
            {/* Sticky config summary + tabs (pin together) */}
            <div className="rc-sticky-head">
              <div className="rc-cfgbar">
                <div className="rc-cfgbar__item"><span>Triggers</span><b>{(rule.trigger_lsids || []).map(statusName).join(', ') || '—'}</b></div>
                <div className="rc-cfgbar__item"><span>Wait</span><b>{rule.duration_min % 60 === 0 ? `${rule.duration_min / 60}h` : `${rule.duration_min}m`}</b></div>
                <div className="rc-cfgbar__item"><span>After rotation</span><b>{statusName(rule.new_status_lsid)}</b></div>
                <div className="rc-cfgbar__item"><span>Dump status</span><b>{statusName(rule.dump_status_lsid)}</b></div>
                <div className="rc-cfgbar__item"><span>Mode</span><b>{rule.auto_assign ? rule.rotation_mode : 'unassign'}</b></div>
                <div className="rc-cfgbar__item"><span>Pool</span><b>{rule.pool_mode === 'team' ? `Team of ${userName(rule.reports_to_uid)}` : (rule.users || []).map(u => u.username).join(', ') || '—'}</b></div>
              </div>

              <div className="rc-tabs">
                {['queue', 'logs'].map(t => (
                  <button key={t} className={`rc-tab ${tab === t ? 'rc-tab--active' : ''}`} onClick={() => setTab(t)}>
                    {t === 'queue' ? 'Queue' : 'Logs'}
                  </button>
                ))}
              </div>

              {tab === 'queue' && (
                <div className="rc-qtoolbar">
                  <span className="rc-qcount">{queue.total} lead{queue.total === 1 ? '' : 's'} in scope</span>
                  <div className="rc-qsearch">
                    <Search size={15} />
                    <input value={queueSearch} onChange={e => setQueueSearch(e.target.value)} placeholder="Search by name or mobile" />
                    {queueSearch && <button type="button" onClick={() => setQueueSearch('')} aria-label="Clear"><X size={14} /></button>}
                  </div>
                </div>
              )}

              {tab === 'logs' && stats && (
                <div className="rc-log-toolbar">
                  <div className="rc-log-chips">
                    {logFilterChips.map(c => {
                      const active = actionFilter === c.key;
                      const Ic = c.icon;
                      return (
                        <button key={c.key || 'all'} className={`rc-log-chip ${active ? 'rc-log-chip--active' : ''}`} onClick={() => setActionFilter(c.key)}>
                          <Ic size={13} />
                          {c.label}{typeof c.count === 'number' ? ` ${c.count}` : ''}
                        </button>
                      );
                    })}
                  </div>
                  <button className="rc-csv" onClick={exportLogsCsv}>Export CSV</button>
                </div>
              )}
            </div>

            {tab === 'queue' && (
              detailLoading ? <div className="rc-loading">Loading queue…</div> : (
                <div className="rc-queue">
                  {queue.items.length === 0 ? (
                    <div className="rc-detail-empty"><Inbox size={26} /><p>{queueSearch ? 'No leads match your search.' : 'Nothing in queue right now.'}</p></div>
                  ) : (
                    <div className="rc-qlist">
                      {queue.items.map(i => (
                        <button
                          key={i.l_id}
                          type="button"
                          className={`rc-qcard ${i.due ? 'rc-qcard--due' : ''}`}
                          onClick={() => navigate(`/leads/edit/${i.l_id}`)}
                          title="Open lead"
                        >
                          <div className="rc-qcard__top">
                            <div className="rc-qcard__id">
                              <span className="rc-qcard__name">{i.lead_name || `Lead #${i.l_id}`}</span>
                              {i.lead_mobile ? <span className="rc-qcard__mobile">{i.lead_mobile}</span> : null}
                              {i.project_names ? <span className="rc-qcard__proj">{i.project_names}</span> : null}
                            </div>
                            {i.status_name ? (
                              <span className="rc-qcard__status" style={{ background: (i.status_color || '#888888') + '22', color: i.status_color || 'var(--gray-600)' }}>
                                {i.status_name}
                              </span>
                            ) : null}
                          </div>
                          <div className="rc-qcard__flow">
                            <User size={13} />
                            <span className="rc-qcard__from">{userName(i.from_uid)}</span>
                            <ArrowRight size={13} />
                            <span className="rc-qcard__to">{i.action === 'rotate' ? userName(i.to_uid) : i.action}</span>
                          </div>
                          <div className={`rc-qcard__time ${i.due ? 'rc-qcard__time--due' : ''}`}>
                            {i.due ? `Rotate at ${fmtDateTime(i.rotate_at)}` : `Rotates in ${fmtCountdown(i.rotate_at)}`}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <Pager meta={queue} onPage={(p) => loadDetail('queue', p)} />
                </div>
              )
            )}

            {tab === 'logs' && (
              <div className="rc-logs">
                {detailLoading ? (
                  <div className="rc-loading">Loading logs…</div>
                ) : logs.length === 0 ? (
                  <div className="rc-detail-empty">
                    <ClipboardList size={26} />
                    <p>{actionFilter ? `No ${actionFilter} logs yet.` : 'No actions logged yet.'}</p>
                  </div>
                ) : (
                  <>
                    <div className="rc-logcards">
                      {logs.map(l => {
                        const Ic = ACTION_ICON[l.action] || Repeat;
                        return (
                          <button
                            key={l.rl_id}
                            type="button"
                            className="rc-logcard"
                            onClick={() => navigate(`/leads/edit/${l.l_id}`)}
                            title="Open lead"
                          >
                            <div className="rc-logcard__top">
                              <span className={`rc-logcard__badge rc-logcard__badge--${l.action}`}>
                                <Ic size={12} /> {l.action}
                              </span>
                              <span className="rc-logcard__time">{fmtDateTime(l.run_at)}</span>
                            </div>
                            <span className="rc-logcard__lead">{l.lead_name || `Lead #${l.l_id}`}{l.lead_mobile ? <span className="rc-logcard__mobile"> · {l.lead_mobile}</span> : null}</span>
                            {l.action === 'rotated' ? (
                              <div className="rc-logcard__flow">
                                <User size={12} />
                                <span className="rc-logcard__from">{l.from_username || 'Unassigned'}</span>
                                <ArrowRight size={12} />
                                <span className="rc-logcard__to">{l.to_username || '—'}</span>
                              </div>
                            ) : (
                              <span className="rc-logcard__note">{l.note || (l.new_status_name ? `Moved to ${l.new_status_name}` : '')}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                    <Pager meta={logsMeta} onPage={(p) => loadDetail('logs', p)} />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default RecycleLeadDetail;
